import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiClock, FiX } from 'react-icons/fi';
import { useAdvancedSearch } from '../hooks/useAdvancedSearch';
import { useProducts } from '../hooks/useProducts';

const SearchBox = ({ className = '', placeholder = 'Search products, categories or brands' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { products } = useProducts();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const {
    searchHistory,
    getSearchSuggestions,
    addToHistory,
    clearHistory,
    getSpellingSuggestions
  } = useAdvancedSearch(products);

  const suggestions = getSearchSuggestions(searchQuery, 6);
  const spellingSuggestions = getSpellingSuggestions(searchQuery);
  const recentSearches = searchHistory.slice(0, 4);

  const allSuggestions = [
    ...suggestions.map((text) => ({ type: 'suggestion', text })),
    ...(searchQuery.trim() && spellingSuggestions.length > 0
      ? spellingSuggestions.map((text) => ({ type: 'spelling', text }))
      : []),
    ...(searchQuery.trim() === ''
      ? recentSearches.map((entry) => ({ type: 'history', text: entry.query }))
      : [])
  ];

  const handleSearch = (query = searchQuery) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    addToHistory(trimmed);
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setSearchQuery('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedIndex >= 0 && selectedIndex < allSuggestions.length) {
      handleSearch(allSuggestions[selectedIndex].text);
      return;
    }
    handleSearch();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < allSuggestions.length - 1 ? prev + 1 : 0));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : allSuggestions.length - 1));
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
    if (e.key === 'Tab' && selectedIndex >= 0) {
      e.preventDefault();
      setSearchQuery(allSuggestions[selectedIndex].text);
      setSelectedIndex(-1);
    }
  };

  useEffect(() => {
    const handleOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleOutside);
    }
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showSuggestions]);

  const iconForSuggestion = (type) => {
    if (type === 'history') return <FiClock className="h-4 w-4 text-slate-400" />;
    return <FiSearch className="h-4 w-4 text-slate-400" />;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <form onSubmit={handleSubmit} className="relative">
        <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => {
            const value = e.target.value;
            setSearchQuery(value);
            setSelectedIndex(-1);
            setShowSuggestions(value.trim().length > 0 || searchHistory.length > 0);
          }}
          onFocus={() => setShowSuggestions(searchQuery.trim().length > 0 || searchHistory.length > 0)}
          onKeyDown={handleKeyDown}
          className="w-full rounded-full border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-11 text-sm text-slate-800 outline-none transition focus:border-[#DB4444] focus:bg-white focus:shadow-[0_0_0_3px_rgba(219,68,68,0.14)]"
          autoComplete="off"
        />
        <button
          type="submit"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-[#1A1A1A] p-2 text-white transition hover:bg-[#DB4444]"
          aria-label="Search"
        >
          <FiSearch className="h-3.5 w-3.5" />
        </button>
      </form>

      {showSuggestions && allSuggestions.length > 0 && (
        <div className="search-dropdown absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
          {recentSearches.length > 0 && searchQuery.trim() === '' && (
            <div className="border-b border-slate-100 px-3 py-2">
              <button
                onClick={clearHistory}
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 transition hover:text-[#DB4444]"
              >
                <FiX className="h-3 w-3" />
                Clear history
              </button>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto">
            {allSuggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.type}-${suggestion.text}-${index}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSearch(suggestion.text)}
                className={`search-suggestion flex w-full items-center gap-3 px-3 py-2.5 text-left ${
                  selectedIndex === index
                    ? 'bg-red-50 text-[#1A1A1A]'
                    : 'hover:bg-slate-50 text-slate-700'
                }`}
              >
                {iconForSuggestion(suggestion.type)}
                <div className="flex-1">
                  {suggestion.type === 'spelling' && (
                    <p className="text-[10px] font-bold uppercase tracking-wide text-[#DB4444]">
                      Did you mean
                    </p>
                  )}
                  {suggestion.type === 'history' && (
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      Recent
                    </p>
                  )}
                  <p className="text-sm">{suggestion.text}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBox;
